import PostRequest from "csi/comm/PostRequest";
import { TYPE_SUSTAINABILITY_PROFILE_REVISION } from "csi/model/sustainabilityProfile";
import { resolveNodes } from "csi/service/nodeService";
import { routerStore } from "csi/state/rootStore";
import { TableWidget } from "csi/table/Widget";
import { C8NodeType, cnl } from "csi/typings";
import { ReactionStore } from "csi/utility/mobxUtility";
import { ViewWidget } from "csi/view/Widget";
import xml from "csi/xml";
import { action, makeObservable, observable } from "mobx";
import { createContext, SyntheticEvent, useContext } from "react";

const MODULE_SPECIFICATION = "Specification";
const PDF_REQUEST_STATUS_FAILED = "Failed";
const PROPERTIES = "Properties";
const RETRY_PDF_GENERATION = "RetryPDFGeneration";
const WHERE_USED = "WhereUsed";

export class SustainabilityProfileStore extends ReactionStore {
	@observable requestFailed: boolean = false;
	@observable selectedTabIndex: number = 0;
	tabPanels: string[] = [PROPERTIES, WHERE_USED];
	viewNodeType: C8NodeType = TYPE_SUSTAINABILITY_PROFILE_REVISION;
	whereUsedView: TableWidget;

	constructor() {
		super();
		makeObservable(this);
	};

	@action handleTabChange = (event: SyntheticEvent, newValue: number): void => {
		this.selectedTabIndex = newValue;
		if (newValue === this.tabPanels.indexOf(WHERE_USED)) {
			setTimeout(() => {
				this.whereUsedView.resize();
			});
		}
	};

	@action setSelectedTabIndex = (index: number): void => {
		this.selectedTabIndex = index;
	};

	@action updateRequestFailed = (failed: boolean): void => {
		this.requestFailed = failed;
	};

	@action updateWhereUsedView = (newWhereUsedView: TableWidget): void => {
		this.whereUsedView = newWhereUsedView;
	};

	get locationRevisionUrl(): cnl {
		const { locationRevisionUrl } = routerStore;
		return locationRevisionUrl;
	};

	get isTemplate(): boolean {
		const { locationNode } = routerStore;
		return xml.isEmptyURL(locationNode.__Parent__);
	};

	/** Resize table widget on load after caching the correct width from the Properties view */
	@action onViewLoad = (view: ViewWidget): void => {
		if (view.defView.m_id === WHERE_USED) {
			this.updateWhereUsedView(view as TableWidget);
		}
	};

	get pdfRequestsExist(): boolean {
		const { locationRevisionNode } = routerStore;
		const pdfRequests = locationRevisionNode.PDFRequests;
		if (pdfRequests.length > 0) {
			this.checkPDFRequestsForFailed(pdfRequests).then((failed) => {
				this.updateRequestFailed(failed);
			});
		}
		return pdfRequests.length > 0;
	};

	get pdfURL(): string {
		const { locationRevisionNode } = routerStore;
		return locationRevisionNode.PDF;
	};

	get revisionState(): string {
		const { locationRevisionNode } = routerStore;
		return locationRevisionNode.State;
	};

	checkPDFRequestsForFailed = async (requestCNLs): Promise<boolean> => {
		const pdfRequestNodes = await resolveNodes(requestCNLs);
		for (const pdfRequest of pdfRequestNodes.reverse()) {
			// if PDFRequest.RecordPDF==true, then this PDFRequest was generated on approval. Check if this request has failed.
			if (pdfRequest.RecordPDF) {
				return pdfRequest.Status === PDF_REQUEST_STATUS_FAILED;
			}
		}
 		return false;
	};

	retryPDFGeneration = (): void => {
		const { locationRevisionNode } = routerStore;
		const request = new PostRequest(MODULE_SPECIFICATION, RETRY_PDF_GENERATION)
			.addParameter(xml.ATTR_CNL, locationRevisionNode.$URL);
		request.post();
	};
}

export const SustainabilityProfileStoreContext = createContext<SustainabilityProfileStore>(new SustainabilityProfileStore());
export const useSustainabilityProfileStore = (): SustainabilityProfileStore => useContext(SustainabilityProfileStoreContext);
